/* ==========================================================================
   pi-utils.js
   Shared helpers for every pi-* component. Globals attached to window.PiUtils
   so non-module scripts can use them. Does NOT touch global.js / cart.js.
   ========================================================================== */

(function () {
  if (window.PiUtils) return;

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    'details summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const scrollLockOwners = new Set();
  let anonymousLockCount = 0;
  let savedScrollY = 0;

  function isScrollLocked() {
    return scrollLockOwners.size > 0 || anonymousLockCount > 0;
  }

  function applyBodyLock() {
    savedScrollY = window.scrollY || document.documentElement.scrollTop;
    document.documentElement.style.setProperty('--pi-saved-scroll', savedScrollY + 'px');
    document.body.classList.add('pi-no-scroll', 'overflow-hidden-mobile');
    // Preserve scroll position by pinning the body.
    document.body.style.position = 'fixed';
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function applyBodyUnlock() {
    document.body.classList.remove('pi-no-scroll', 'overflow-hidden-mobile');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, savedScrollY);
  }

  function lockScroll(owner) {
    const wasLocked = isScrollLocked();
    if (owner != null) {
      if (scrollLockOwners.has(owner)) return;
      scrollLockOwners.add(owner);
    } else {
      anonymousLockCount++;
    }
    if (!wasLocked) applyBodyLock();
  }

  function unlockScroll(owner) {
    if (owner != null) {
      if (!scrollLockOwners.has(owner)) return;
      scrollLockOwners.delete(owner);
    } else {
      anonymousLockCount = Math.max(0, anonymousLockCount - 1);
    }
    if (!isScrollLocked()) applyBodyUnlock();
  }

  function getFocusable(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => {
      const rect = el.getBoundingClientRect();
      const visible = rect.width > 0 || rect.height > 0 || el === document.activeElement;
      return visible && !el.hasAttribute('aria-hidden');
    });
  }

  function trapFocus(container, initialFocus) {
    if (!container) return () => {};
    const focusables = getFocusable(container);
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const target = initialFocus || first || container;
    if (target && typeof target.focus === 'function') {
      requestAnimationFrame(() => target.focus({ preventScroll: true }));
    }

    function onKey(e) {
      if (e.key !== 'Tab') return;
      const list = getFocusable(container);
      if (list.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = list[0];
      const lastEl = list[list.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }

    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  }

  function formatMoney(cents, format) {
    // Falls back to a sane USD-style format. For accurate currency formatting,
    // prefer reading the formatted string from Shopify section re-renders.
    if (typeof cents !== 'number') cents = parseFloat(cents) || 0;
    const value = (cents / 100).toFixed(2);
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, format);
    }
    return '$' + value;
  }

  function debounce(fn, wait) {
    let t;
    return function () {
      const args = arguments;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function onceVisible(el, callback) {
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callback();
          io.disconnect();
        }
      });
    });
    io.observe(el);
  }

  function initPiFcSlider(scope) {
    var root = scope || document;
    root.querySelectorAll('.pi-fc__slider-wrapper:not([data-pi-fc-init])').forEach(function (wrapper) {
      wrapper.setAttribute('data-pi-fc-init', '');

      var track = wrapper.querySelector('.pi-fc__track');
      if (!track) return;

      var arrows = wrapper.querySelectorAll('.pi-fc__arrow');

      arrows.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = track.querySelector('.pi-fc__item');
          if (!item) return;
          var gap = parseInt(getComputedStyle(track).gap, 10) || 0;
          var scrollAmount = (item.offsetWidth + gap) * 2;
          track.scrollBy({
            left: btn.dataset.direction === 'next' ? scrollAmount : -scrollAmount,
            behavior: 'smooth',
          });
        });
      });

      function updateArrows() {
        var atStart = track.scrollLeft <= 1;
        var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        arrows.forEach(function (btn) {
          if (btn.dataset.direction === 'prev') btn.disabled = atStart;
          if (btn.dataset.direction === 'next') btn.disabled = atEnd;
        });
      }

      track.addEventListener('scroll', updateArrows, { passive: true });
      updateArrows();
    });
  }

  window.PiUtils = {
    lockScroll,
    unlockScroll,
    trapFocus,
    getFocusable,
    formatMoney,
    debounce,
    onceVisible,
    initPiFcSlider,
  };

  document.addEventListener('DOMContentLoaded', function () {
    initPiFcSlider(document);
  });
})();
